import { dayjs } from '#gp/app-common/util/dayjs';
import { gpHome } from '../app';
import { formatRelativeTimeInPast } from '../../app-common/util/dateFormatting';
import { IProjectService, IProject } from '../../app-common/services/projects-service';
import { IRouteAuthService, IRouteAuthServiceEvents } from '../services/route-auth-service';
import { ILogglyService } from '../../app-common/services/jsloggly-service';
import { SyncHelper } from '../../app-common/util/sync';
import { SimpleProject } from '../models/simple-project';
import { PlanListCache } from '../services/plan-list-cache';
import { IAuthorisationStatus } from '../../app-common/services/authorisation-checker-with-caching-service';
import { computePlanListEmptyState, PlanListEmptyState } from '#gp/app-home/models/plan-list-empty-state';
import { shouldAutoStartFirstPlan } from './first-plan-autostart';
import { isAnyAiFeatureFlagEnabled } from '#gp/app-common/util/feature-flags';
import { arraySortBy } from '#gp/app-common/util/arrays';
import { sortKeyCompareOption } from '#gp/app-common/util/text-collation';

export { SimpleProject };

interface IProjectsControllerScope extends ng.IScope {
    debugMsg: string;

    selectedFilter: string;
    searchText: string;
    sortKey: string;

    filteredProjects: SimpleProject[];
    myProjects: SimpleProject[];
    deletedProjects: SimpleProject[];
    sharedProjects: SimpleProject[];
    plansLoading: boolean;
    emptyState: PlanListEmptyState | null;
    currentUserEmail: string | null;

    addProject(): void;

    gotoSubscriptions(): void;
    gotoAccounts(): void;

    refreshProjects(): void;
    changePlanFilter(filter: string): void;
    isFilterActive(filter: string): boolean;
    filtersChanged(): void;
    clearSearch(): void;

    countAll(): number;
    countMine(): number;
    countShared(): number;
    countDeleted(): number;
    showSharedPill(): boolean;
    showDeletedPill(): boolean;
}

class BoardsController {
    private currentUserEmail: string | null = null;
    private planListCache = new PlanListCache(localStorage, () => dayjs().unix());
    // True only when the latest refresh round saw all three list requests
    // genuinely succeed; the service resolves [] on failure, and auto-start
    // must never mistake an outage for an empty account.
    private allListsLoaded = false;
    private readonly authEventOptions: IRouteAuthServiceEvents = {
        loggedIn: (status: IAuthorisationStatus) => {
            this.onLoggedIn(status);
        },
        loggedOut: (email: string | null) => {
            this.onLoggedOut(email);
        },
    };

    constructor(
        public $scope: IProjectsControllerScope,
        private $q: ng.IQService,
        private $location: ng.ILocationService,
        private $route: ng.route.IRouteService,
        public projectsService: IProjectService,
        private gpRouteAuthService: IRouteAuthService,
        private $sce: ng.ISCEService,
        private gpLogglyService: ILogglyService,
        public $timeout: ng.ITimeoutService,
    ) {

        let me = this;

        $scope.searchText = '';
        $scope.sortKey = 'lastSaved';
        $scope.filteredProjects = [];
        $scope.myProjects = [];
        $scope.deletedProjects = [];
        $scope.sharedProjects = [];
        $scope.plansLoading = true;
        $scope.emptyState = null;
        $scope.currentUserEmail = null;
        $scope.addProject = () => me.addProject();

        $scope.gotoSubscriptions = () => $location.path('/subscribe');
        $scope.gotoAccounts = () => $location.path('/account');

        $scope.refreshProjects = () => this.updateProjectsList();
        $scope.changePlanFilter = (filter) => this.changePlanFilter(filter);
        $scope.isFilterActive = (filter) => ($scope.selectedFilter || 'all') === filter;
        $scope.filtersChanged = () => this.updateFilteredList();
        $scope.clearSearch = () => {
            $scope.searchText = '';
            this.updateFilteredList();
        };

        $scope.countAll = () => $scope.myProjects.length + $scope.sharedProjects.length;
        $scope.countMine = () => $scope.myProjects.length;
        $scope.countShared = () => $scope.sharedProjects.length;
        $scope.countDeleted = () => $scope.deletedProjects.length;
        // A pill with a permanent zero count is noise, so Shared/Archived only
        // appear once they have contents. They stay while selected so an empty
        // result can never strand the user on a filter with no pill.
        $scope.showSharedPill = () => __gameplan_global_enable_plan_sharing
            && ($scope.sharedProjects.length > 0 || $scope.selectedFilter === 'shared');
        $scope.showDeletedPill = () => $scope.deletedProjects.length > 0 || $scope.selectedFilter === 'deleted';

        this.gpRouteAuthService.register(this.authEventOptions);
        $scope.$on('gp-plan-list-possibly-changed', () => this.updateProjectsList());

        // Plans are usually edited in another tab; refresh the list when the
        // user comes back to it so saves show up without a manual reload.
        const onReturnToTab = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            $scope.$applyAsync(() => this.refreshIfStale());
        };
        window.addEventListener('focus', onReturnToTab);
        document.addEventListener('visibilitychange', onReturnToTab);

        $scope.$on('$destroy', () => {
            window.removeEventListener('focus', onReturnToTab);
            document.removeEventListener('visibilitychange', onReturnToTab);
            this.gpRouteAuthService.deregister(this.authEventOptions);
        });
    }

    // Throttled so rapid focus/visibility flapping does not hammer the API.
    private lastListRefreshUnix = 0;
    private static readonly LIST_REFRESH_THROTTLE_SECONDS = 10;

    private refreshIfStale() {
        if (!this.currentUserEmail) {
            return;
        }
        const now = dayjs().unix();
        if (now - this.lastListRefreshUnix < BoardsController.LIST_REFRESH_THROTTLE_SECONDS) {
            return;
        }
        this.updateProjectsList();
    }

    addProject() {
        this.$location.path("/plans/new");
    }

    private onLoggedIn(status: IAuthorisationStatus) {
        this.currentUserEmail = status ? status.email : null;
        this.$scope.currentUserEmail = this.currentUserEmail;
        this.primeFromCache();
        this.updateProjectsList();
    }

    private onLoggedOut(email: string | null) {
        const toClear = email || this.currentUserEmail;
        if (toClear) {
            this.planListCache.clear(toClear);
        }
        this.$location.path("/login");
    }

    private primeFromCache() {
        if (!this.currentUserEmail) {
            return;
        }
        const cached = this.planListCache.read(this.currentUserEmail);
        if (!cached) {
            return;
        }
        this.$scope.myProjects = cached.myProjects;
        this.$scope.sharedProjects = cached.sharedProjects;
        this.$scope.deletedProjects = cached.deletedProjects;
        this.updateFilteredList();
    }

    navigateToEditingNewTab(p: IProject) {
        let newurl = window.location.protocol + "//" + window.location.host + "/projects/" + p.id + "/edit";
        window.open(newurl);
    }

    private changePlanFilter(newFilter: string) {
        this.$scope.selectedFilter = newFilter;
        this.updateFilteredList();
    }

    private static convertProjects(srcProjects: IProject[], destProjects: SimpleProject[], editable: boolean, owned: boolean, projectType: string): void {

        const i = srcProjects.map(p => ({ getID: () => <string>p.id, p: p, when: p.lastEditedUTC ? p.lastEditedUTC.unix() : 0 }));

        SyncHelper.syncArraysWithId(i, destProjects,
            srcItem => new SimpleProject(srcItem.p.id || ''),
            (srcItem, destItem) => BoardsController.updateItem(srcItem.p, srcItem.when, editable, owned, projectType, destItem),
            destItem => { }
        );
    }

    updateProjectsList() {

        this.lastListRefreshUnix = dayjs().unix();
        this.$scope.plansLoading = true;
        this.allListsLoaded = false;

        const myRequest = this.projectsService.getProjectsChecked().then(result => {
            BoardsController.convertProjects(result.projects, this.$scope.myProjects, true, true, "myproject");
            this.updateFilteredList();
            return result.succeeded;
        });

        const deletedRequest = this.projectsService.getDeletedProjectsChecked().then(result => {
            BoardsController.convertProjects(result.projects, this.$scope.deletedProjects, false, true, "deleted");
            this.updateFilteredList();
            return result.succeeded;
        });

        const sharedRequest = this.projectsService.getSharedProjectsChecked().then(result => {
            BoardsController.convertProjects(result.projects, this.$scope.sharedProjects, true, false, "sharedwithme");
            this.updateFilteredList();
            return result.succeeded;
        });

        this.$q.all([myRequest, deletedRequest, sharedRequest]).then(succeeded => {
            this.allListsLoaded = succeeded.every(s => s);
        }).finally(() => {
            this.$scope.plansLoading = false;
            // Only cache a round where every request succeeded; a failed round
            // resolves empty lists and would wipe the useful cached copy.
            if (this.currentUserEmail && this.allListsLoaded) {
                this.planListCache.write(this.currentUserEmail, {
                    myProjects: this.$scope.myProjects,
                    sharedProjects: this.$scope.sharedProjects,
                    deletedProjects: this.$scope.deletedProjects,
                });
            }
            this.maybeAutoStartFirstPlan();
        });
    }

    // A brand-new user gets no value from an empty list, so once the server
    // has confirmed they have no plans at all we take them straight into
    // creating their first one. Once per session: backing out of the new-plan
    // flow must still be able to reach the list.
    private maybeAutoStartFirstPlan() {
        if (this.$location.path() !== '/plans') {
            return;
        }
        const autoStart = shouldAutoStartFirstPlan({
            myCount: this.$scope.myProjects.length,
            sharedCount: this.$scope.sharedProjects.length,
            deletedCount: this.$scope.deletedProjects.length,
            allListsLoaded: this.allListsLoaded,
            alreadyAutoStarted: this.readAutoStartFlag(),
        });
        if (autoStart) {
            this.writeAutoStartFlag();
            this.$location.path('/plans/new');
        }
    }

    private autoStartFlagKey() {
        return `_gp_first_plan_autostart_${this.currentUserEmail || ''}`;
    }

    private readAutoStartFlag(): boolean {
        try {
            return !!window.sessionStorage.getItem(this.autoStartFlagKey());
        } catch (e) {
            // If storage is unavailable we cannot remember having auto-started,
            // so report "already started" rather than risk a redirect loop.
            return true;
        }
    }

    private writeAutoStartFlag() {
        try {
            window.sessionStorage.setItem(this.autoStartFlagKey(), '1');
        } catch (e) {
            // best-effort only; readAutoStartFlag fails closed
        }
    }

    updateFilteredList() {

        let newFilter = this.$scope.selectedFilter || 'all';
        let result: SimpleProject[] = [];

        if (newFilter === 'all' || newFilter === 'mine') {
            result = result.concat(this.$scope.myProjects);
        }

        if (newFilter === 'all' || newFilter === 'shared') {
            result = result.concat(this.$scope.sharedProjects);
        }

        if (newFilter === 'deleted') {
            result = result.concat(this.$scope.deletedProjects);
        }

        const needle = (this.$scope.searchText || '').trim().toLowerCase();
        if (needle) {
            result = result.filter(p => BoardsController.matchesSearch(p, needle));
        }

        this.$scope.filteredProjects = this.sortProjects(result);

        this.$scope.emptyState = this.$scope.filteredProjects.length
            ? null
            : computePlanListEmptyState({
                filter: newFilter,
                searchActive: !!needle,
                myCount: this.$scope.myProjects.length,
                sharedCount: this.$scope.sharedProjects.length,
                deletedCount: this.$scope.deletedProjects.length,
                    aiEnabled: isAnyAiFeatureFlagEnabled(),
            });
    }

    private static matchesSearch(p: SimpleProject, needle: string): boolean {
        const haystacks = [p.name, p.description, p.ownerEmail, p.lastEditedUserEmail];
        return haystacks.some(s => !!s && s.toLowerCase().indexOf(needle) >= 0);
    }

    private sortProjects(list: SimpleProject[]): SimpleProject[] {
        // The multi-key cases sort one key at a time, least significant first:
        // arraySortBy is a stable sort, so each pass preserves the previous order.
        const newestFirst = { ...sortKeyCompareOption, direction: 'desc' } as const;
        switch (this.$scope.sortKey) {
            case 'name':
                return arraySortBy(list, p => (p.name || '').toLowerCase(), sortKeyCompareOption);
            case 'start':
                // Plans without dates sort to the end rather than the top.
                return arraySortBy(list, p => p.planStartUnix || Number.MAX_SAFE_INTEGER, sortKeyCompareOption);
            case 'end':
                return arraySortBy(list, p => p.planEndUnix || Number.MAX_SAFE_INTEGER, sortKeyCompareOption);
            case 'owner': {
                // Your own plans first, then each sharer's plans grouped
                // alphabetically; most recently saved first within a group.
                const byRecency = arraySortBy(list, p => p.lastEditedUnix, newestFirst);
                return arraySortBy(byRecency, p => p.isOwnedByMe ? '' : (p.ownerEmail || '').toLowerCase(), sortKeyCompareOption);
            }
            default: {
                const byName = arraySortBy(list, p => (p.name || '').toLowerCase(), sortKeyCompareOption);
                return arraySortBy(byName, p => p.lastEditedUnix, newestFirst);
            }
        }
    }

    private static toLastEditedText(utc: dayjs.Dayjs | null) {
        if (utc) {
            return "Last saved: " + formatRelativeTimeInPast(utc, true);
        } else {
            return null;
        }
    }

    private static toPlanDate(utc: dayjs.Dayjs | null, includeYear: boolean) {
        if (utc) {
            if (includeYear) {
                return utc.local().format("D MMM YYYY");
            } else {
                return utc.local().format("D MMM");
            }
        } else {
            return null;
        }
    }

    private static updateItem = (srcItem: IProject, srcWhen: number, editable: boolean, owned: boolean, projectType: string, destItem: SimpleProject) => {

        const thisYear = new Date().getUTCFullYear();
        const startYear = srcItem.planStartUTC ? srcItem.planStartUTC.year() : thisYear;
        const endYear = srcItem.planEndUTC ? srcItem.planEndUTC.year() : thisYear;
        const showYear = (startYear !== thisYear || endYear !== thisYear);

        destItem.accessLevel = srcItem.accessLevel;
        destItem.canEdit = editable;
        destItem.description = srcItem.description || '';
        destItem.isDeleted = !editable;
        destItem.isOwnedByMe = owned;
        destItem.isShared = srcItem.isShared;
        destItem.lastEdited = BoardsController.toLastEditedText(srcItem.lastEditedUTC) || '';
        destItem.lastEditedUserEmail = srcItem.lastEditedUserEmail;
        destItem.lastEditedUnix = srcWhen;
        destItem.name = srcItem.name;
        destItem.projectType = projectType;
        // Version the thumbnail URL by last-saved time so a saved plan defeats
        // the browser's cached image; the endpoint ignores unknown params.
        destItem.thumbnailUrl = srcItem.thumbnailUrl
            ? srcItem.thumbnailUrl + (srcItem.thumbnailUrl.indexOf('?') >= 0 ? '&' : '?') + 'v=' + srcWhen
            : srcItem.thumbnailUrl;
        destItem.planStart = BoardsController.toPlanDate(srcItem.planStartUTC, showYear) || '';
        destItem.planEnd = BoardsController.toPlanDate(srcItem.planEndUTC, showYear) || '';
        destItem.planStartUnix = srcItem.planStartUTC ? srcItem.planStartUTC.unix() : 0;
        destItem.planEndUnix = srcItem.planEndUTC ? srcItem.planEndUTC.unix() : 0;
        destItem.workHours = Math.round(srcItem.workHours);
        destItem.percentComplete = Math.round(srcItem.percentComplete);
        destItem.subprojectCount = srcItem.subprojectCount;
        destItem.ownerEmail = srcItem.ownerEmail;
    }
}



gpHome.controller('boardsController', [
    '$scope',
    '$q',
    '$location',
    '$route',
    'projectsService',
    'gpRouteAuthService',
    '$sce',
    'gpLogglyService',
    '$timeout',
    function (
        $scope: IProjectsControllerScope,
        $q: ng.IQService,
        $location: ng.ILocationService,
        $route: ng.route.IRouteService,
        projectsService: IProjectService,
        gpRouteAuthService: IRouteAuthService,
        $sce: ng.ISCEService,
        gpLogglyService: ILogglyService,
        $timeout: ng.ITimeoutService,
    ) {

        new BoardsController($scope, $q, $location, $route, projectsService, gpRouteAuthService, $sce, gpLogglyService, $timeout)
    }]);
